import type { FC } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQuery } from "@apollo/client";
import { ContentsList } from "@/shared/components/blocks/ContentsBlock";
import { useContents } from "@/shared/hooks/use-contents/useContents";
import { useTranslation } from "@/shared/hooks";
import { CollectionPlaylistQuery } from "@/entities/contents";
import { Link } from "@/shared/components/RouteDialog";
import {
  type CollectionPlaylistQueryType,
  CollectionContentSortType,
  ContentSortTypeType,
} from "@/types/graphql/site.generated";

// Режим коллекции → sort списка (порт референсного ContentPlaylist:
// sort = collectionContentSort || LATEST)
const PLAYLIST_SORTS: Record<CollectionContentSortType, ContentSortTypeType> = {
  [CollectionContentSortType.LatestType]: ContentSortTypeType.LatestType,
  [CollectionContentSortType.OldestType]: ContentSortTypeType.OldestType,
  [CollectionContentSortType.UpdatedType]: ContentSortTypeType.UpdatedType,
  [CollectionContentSortType.ManualType]: ContentSortTypeType.ManualType,
};

// Плейлист коллекции в диалоге видео (порт референсного ContentPlaylist):
// шапка с названием коллекции и стрелкой «назад» на её страницу, ниже —
// список видео коллекции в её порядке; ссылки сохраняют ?collectionId=,
// чтобы плейлист жил при переходах между видео.
export const ContentPlaylist: FC<{
  collectionId: string;
  variant: "sidebar" | "mobile";
}> = ({ collectionId, variant }) => {
  const { t } = useTranslation();
  const { data } = useQuery<CollectionPlaylistQueryType>(
    CollectionPlaylistQuery,
    { variables: { id: collectionId } },
  );
  const collection = data?.content;

  const sort =
    PLAYLIST_SORTS[
      collection?.collectionContentSort ?? CollectionContentSortType.LatestType
    ];

  const { contents, loading, hasMore, loadMore, called } = useContents({
    limit: 12,
    sort,
    collectionIds: [collectionId],
    skip: !collection,
  });

  if (contents.length === 0 && called && !loading) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: variant === "mobile" ? { xs: 1.875, sm: 3 } : 0,
          pb: 1,
        }}
      >
        {collection && (
          <IconButton
            component={Link}
            href={collection.path}
            size="small"
            aria-label={t("video.playlist.backToCollection")}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
          {collection?.title ?? t("video.playlist.title")}
        </Typography>
      </Box>
      <Box sx={variant === "mobile" ? { py: 0 } : { p: 0 }}>
        <ContentsList
          items={contents}
          previewMode="HORIZONTAL"
          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loading={loading}
          collectionId={collectionId}
        />
      </Box>
    </>
  );
};
